import { process, State } from '@progress/kendo-data-query';
import { type FC, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import CardGridWrapper from 'src/components/CardGridWrapper';
import useRedirect from 'src/hooks/useRedirect';
import PunchoutErrorDialog from 'src/sections/punchout_order_guide/PunchoutErrorDialog';
import { usePunchoutExternalSiteRedirect } from 'src/sections/punchout_order_guide/usePunchoutRedirect';
import { ExternalCatalogResponse } from 'src/services/catalog_products/useGetExternalCatalogMutation';
import { SupplierCatalog } from 'src/types/view_catalogs/SupplierCatalog';
import { useContextSelector } from 'use-context-selector';
import CatalogSupplierInfoSidePanel from './CatalogSupplierInfoSidePanel';
import SupplierCatalogCard from './SupplierCatalogCard';
import { ViewCatalogsContext } from './ViewCatalogsContext';
import ViewCatalogsGridHeader from './ViewCatalogsGridHeader';

const ViewCatalogsGridView: FC = () => {
  const { t } = useTranslation();
  const { redirect, getUrl } = useRedirect();
  const [punchoutError, setPunchoutError] = useState<ExternalCatalogResponse | null>(null);
  const {
    openPunchout,
    isPending: isOpeningPunchout,
    openingCatalogId,
  } = usePunchoutExternalSiteRedirect({
    errorMessage: t('Unable to open the external catalog. Please connect with the administrator.'),
    onFailure: setPunchoutError,
  });

  const closePunchoutError = useCallback(() => {
    setPunchoutError(null);
  }, []);

  const catalogs = useContextSelector(ViewCatalogsContext, (s) => s.catalogs);
  const catalogDataState = useContextSelector(ViewCatalogsContext, (s) => s.catalogDataState);
  const isGridLoading = useContextSelector(ViewCatalogsContext, (s) => s.isGridLoading);
  const dataState = useContextSelector(ViewCatalogsContext, (s) => s.dataState);
  const setDataState = useContextSelector(ViewCatalogsContext, (s) => s.setDataState);
  const openSupplierInfo = useContextSelector(ViewCatalogsContext, (s) => s.openSupplierInfo);
  const openProgramInfo = useContextSelector(ViewCatalogsContext, (s) => s.openProgramInfo);
  const searchText = useContextSelector(ViewCatalogsContext, (s) => s.searchText);
  const hasSearchText = searchText && searchText.trim();

  const suppliers = useMemo(
    () => process(catalogs, catalogDataState).data as SupplierCatalog[],
    [catalogDataState, catalogs]
  );

  const onDataStateChange = useCallback(
    (nextState: State) => {
      setDataState(nextState);
    },
    [setDataState]
  );

  const handleBrowseCatalog = useCallback(
    (supplier: SupplierCatalog) => {
      redirect(getUrl('viewProductList')(String(supplier.catalogId)), false, false);
    },
    [getUrl, redirect]
  );

  const handleOpenPunchout = useCallback(
    (supplier: SupplierCatalog) => {
      openPunchout(supplier.catalogId);
    },
    [openPunchout]
  );

  const handleSupplierInfoOpen = useCallback(
    (supplier: SupplierCatalog) => {
      openSupplierInfo(supplier);
    },
    [openSupplierInfo]
  );

  const handleProgramInfo = useCallback(
    (supplier: SupplierCatalog) => {
      if (supplier.programInfo) {
        openProgramInfo(supplier.programInfo);
      }
    },
    [openProgramInfo]
  );

  const renderCard = useCallback(
    (supplier: SupplierCatalog) => (
      <SupplierCatalogCard
        key={supplier.catalogId}
        supplier={supplier}
        onBrowseCatalog={handleBrowseCatalog}
        onOpenPunchout={handleOpenPunchout}
        onSupplierInfo={handleSupplierInfoOpen}
        onProgramInfo={handleProgramInfo}
        isPunchoutLoading={isOpeningPunchout && openingCatalogId === supplier.catalogId}
      />
    ),
    [
      handleBrowseCatalog,
      handleOpenPunchout,
      handleSupplierInfoOpen,
      handleProgramInfo,
      isOpeningPunchout,
      openingCatalogId,
    ]
  );

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="flex min-h-0 flex-1 flex-col px-4 pb-4">
        {hasSearchText && (
          <div className="mb-2 shrink-0 truncate text-sm font-[600]">
            {t('Showing results for')} &quot;{searchText.trim()}&quot;
          </div>
        )}
        <CardGridWrapper<SupplierCatalog>
          dataSource={suppliers}
          renderItem={renderCard}
          dataState={dataState}
          onDataStateChange={onDataStateChange}
          pageable={false}
          loading={isGridLoading}
          className="min-h-0 flex-1"
          tableTextId="Suppliers"
          gap="gap-x-3 gap-y-2"
          height="100%"
          headerChildren={<ViewCatalogsGridHeader />}
          noRecordText={t('No catalogs found.')}
        />
      </div>
      <CatalogSupplierInfoSidePanel />
      <PunchoutErrorDialog response={punchoutError} onClose={closePunchoutError} />
    </div>
  );
};

export default ViewCatalogsGridView;
